// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// ConvexBridge — per-user backend switch + parity comparison (#191).
//
// Mounted whenever Convex is configured. Reads the signed-in user's
// `backendMode` (mongoOnly | convexOnly | both — see backendConfig) and behaves
// accordingly. This mirrors the Realm->TanStack `conversion` model exactly:
//
//   mongoOnly  → INERT. No Convex subscriptions, pollers untouched. Today's app.
//   convexOnly → opens Convex websocket subscriptions and writes each result
//                into the EXACT existing TanStack cache key (same {message:
//                {result}} envelope the Lambda returns), so every existing
//                select + useEffect runs unchanged. Pollers turned OFF.
//   both       → opens Convex subscriptions but does NOT inject (Mongo/Lambda
//                still drives the UI). Instead it COMPARES the Convex result
//                against the Mongo result already in the cache and logs the
//                parity outcome to Axiom (wide-event) + the Convex comparisons
//                table. Pollers stay ON. Proof-of-parity, zero UI change.
//
// Renders nothing. Reads/writes for the new backend are gated by the same
// per-user field as the original migration, in the same codebase.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

import { useContext, useEffect, useRef } from "react";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import { useQueryClient } from "@tanstack/react-query";
import { AppContext } from "./app.context";
import { ModelCxContext } from "./modelCx.context";
import { refs } from "../convex/apiRefs";
import { decodeConvexList, decodeConvexDoc } from "../convex/decodeConvexDoc";
import { CONVEX_SOURCE, resolveBackendMode } from "../utility/backendConfig";
import { MONGODB_ATTRIBUTES_DAYS_PAST } from "../utility/keys";
import { compareResults } from "../utility/convexParityDiff";
import { createWideEvent } from "../utility/wideEventLogger";

// Wrap a Convex result in the Lambda's HTTP envelope so the existing `select`
// (raw => raw.message.result) extracts it verbatim.
function envelope(result) {
  return { message: { result }, _executionTime: 0, _convexBridge: true };
}

// Pull the Mongo-side result for a key prefix out of the TanStack cache, then
// unwrap the {message:{result}} envelope to match a Convex result.
function mongoResultFromCache(qc, prefix) {
  const entries = qc.getQueriesData({ queryKey: prefix });
  for (const [, value] of entries) {
    if (value && value.message && "result" in value.message && !value._convexBridge) {
      return value.message.result;
    }
  }
  return undefined;
}

export default function ConvexBridge() {
  const { companyCode, locationFilter } = useContext(AppContext);
  const { setShouldPollProjects, setShouldPollAttributes, setLatestChangeTS, tsUserDocument } =
    useContext(ModelCxContext);
  const qc = useQueryClient();
  const recordComparison = useConvexMutation(refs.recordComparison);

  // A→B (#388): `source` is NO LONGER a query argument — the BOX is the env, so
  // every Scheduler read below is source-less. It survives only as a LABEL on the
  // parity telemetry ("which env produced this comparison"), never as an arg.
  const source = CONVEX_SOURCE;
  const mode = resolveBackendMode(tsUserDocument);
  const active = mode === "convexOnly" || mode === "both";
  const inject = mode === "convexOnly";
  const compare = mode === "both";

  const hasCo = active && !!companyCode;
  const hasLoc = active && !!companyCode && !!locationFilter;
  const skip = "skip"; // Convex sentinel: don't subscribe

  // - - - - live subscriptions (only when this user uses Convex at all)
  const locations = useConvexQuery(
    refs.getUniqueLocations,
    hasCo ? { companyCode } : skip
  );
  const officeMeta = useConvexQuery(
    refs.getOfficeMetaDataByLocation,
    hasLoc ? { locationCode: locationFilter } : skip
  );
  const users = useConvexQuery(refs.getUsers, hasCo ? { maxLimit: 10000 } : skip);
  const projectsByCompany = useConvexQuery(
    refs.getProjectsByCompany,
    hasLoc ? { companyCode, location: locationFilter } : skip
  );
  const projectsWithShared = useConvexQuery(
    refs.getProjectsByCompanyWithShared,
    hasLoc ? { companyCode, location: locationFilter } : skip
  );
  const candidates = useConvexQuery(
    refs.getCandidatesByCompany,
    hasLoc ? { companyCode, location: locationFilter } : skip
  );
  // #328/#326 — scope the attributes subscription to the SAME daysPast window the
  // app's Lambda query uses (MONGODB_ATTRIBUTES_DAYS_PAST). Without it, Convex
  // returned ALL history (thousands of docs incl. months-old values), which both
  // (a) flapped against the intended recent-scoped set → the grid showed old
  // custom notes that then vanished, and (b) was a needless large read. Now it
  // matches the filtered scope, so the live set is stable + small.
  const attributes = useConvexQuery(
    refs.getAttributesByLocation,
    hasLoc
      ? { companyCode, location: locationFilter, daysPast: MONGODB_ATTRIBUTES_DAYS_PAST }
      : skip
  );

  // - - - - in convexOnly mode, stop polling (Convex pushes updates)
  useEffect(() => {
    if (!inject) return;
    setShouldPollProjects && setShouldPollProjects(false);
    setShouldPollAttributes && setShouldPollAttributes(false);
  }, [inject, setShouldPollProjects, setShouldPollAttributes]);

  // - - - - convexOnly: drive the "Updated: X ago" label from LIVE pushes.
  // With polling off, the timestamp must reflect actual data changes. A Convex
  // useQuery result reference only changes when the server pushes new data, so
  // each change here is a genuine live update — stamp it (this is more accurate
  // than the old poll-tick timestamp). Fires once on first load, then on each
  // real grid change. Skips while still loading (all undefined).
  useEffect(() => {
    if (!inject) return;
    if (projectsWithShared === undefined && candidates === undefined && attributes === undefined) return;
    setLatestChangeTS && setLatestChangeTS(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inject, projectsWithShared, candidates, attributes]);

  // - - - - shared handler: inject (convexOnly) or compare+log (both)
  // Guard against spamming the comparison log: only record when the diff
  // signature changes.
  const lastSigRef = useRef({});

  const handle = (method, kind, prefix, convexResult, label) => {
    if (convexResult === undefined) return;
    if (inject) {
      qc.setQueriesData(prefix.length ? { queryKey: prefix } : { queryKey: [method] }, envelope(convexResult));
      return;
    }
    if (compare) {
      const mongoResult = mongoResultFromCache(qc, prefix.length ? prefix : [method]);
      if (mongoResult === undefined) return; // Mongo hasn't loaded yet; nothing to compare
      const cmp = compareResults(method, mongoResult, convexResult);
      const sig = `${method}|${label}|${cmp.match}|${cmp.diffSummary}`;
      if (lastSigRef.current[method] === sig) return;
      lastSigRef.current[method] = sig;

      // Axiom wide-event (same channel as the Realm/TanStack comparison)
      const ev = createWideEvent("convex-parity");
      ev.set("_experiment", "#191");
      ev.set("kind", kind);
      ev.set("method", method);
      ev.set("label", label);
      ev.set("source", source);
      ev.set("match", cmp.match);
      ev.set("mongoCount", cmp.mongoCount);
      ev.set("convexCount", cmp.convexCount);
      ev.set("diffSummary", cmp.diffSummary);
      ev.set("user", tsUserDocument?.username);
      ev.send && ev.send();

      // Convex comparisons table (queryable dashboard)
      recordComparison({
        kind,
        method,
        label,
        match: cmp.match,
        diffSummary: cmp.diffSummary,
        mongoCount: cmp.mongoCount,
        convexCount: cmp.convexCount,
        user: tsUserDocument?.username,
        createdMs: Date.now(),
      }).catch(() => {});
    }
  };

  const co = companyCode;
  const loc = locationFilter;

  useEffect(() => {
    handle("getUniqueLocations", "query", ["getUniqueLocations", co],
      locations === undefined ? undefined : decodeConvexList(locations), co);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, co, mode]);

  useEffect(() => {
    handle("getOfficeMetaDataByLocation", "query", ["getOfficeMetaDataByLocation", loc],
      officeMeta === undefined ? undefined : (officeMeta ? decodeConvexDoc(officeMeta) : null), loc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeMeta, loc, mode]);

  useEffect(() => {
    handle("getUsers", "query", ["getUsers"],
      users === undefined ? undefined : decodeConvexList(users), co);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, co, mode]);

  useEffect(() => {
    handle("getProjectsByCompany", "query", ["getProjectsByCompany", co, loc],
      projectsByCompany === undefined ? undefined : decodeConvexList(projectsByCompany), `${co}/${loc}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsByCompany, co, loc, mode]);

  useEffect(() => {
    const result = projectsWithShared === undefined ? undefined : {
      projects: decodeConvexList(projectsWithShared.projects || []),
      meta: projectsWithShared.meta || {},
    };
    handle("getProjectsByCompanyWithShared", "query", ["getProjectsByCompanyWithShared", co, loc], result, `${co}/${loc}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsWithShared, co, loc, mode]);

  useEffect(() => {
    handle("getCandidatesByCompany", "query", ["getCandidatesByCompany", co, loc],
      candidates === undefined ? undefined : decodeConvexList(candidates), `${co}/${loc}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, co, loc, mode]);

  useEffect(() => {
    const result = attributes === undefined ? undefined : decodeConvexList(attributes);
    // attributes feeds two query keys (one-shot + polling)
    handle("getAttributesByLocation", "query", ["getAttributesByLocation", co, loc], result, `${co}/${loc}`);
    if (inject && result !== undefined) {
      qc.setQueriesData({ queryKey: ["getAttributesByLocationPolling", co, loc] }, envelope(result));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, co, loc, mode]);

  return null;
}
